/**
 * Teams CRUD — create, browse, join, leave lifting crews.
 */
import { supabase } from '@/lib/supabase';
import { isRateLimited } from './rateLimiter';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface Team {
  id: string;
  name: string;
  tag: string | null;
  description: string | null;
  logo_url: string | null;
  captain_id: string;
  gym_id: string | null;
  region_id: string | null;
  max_members: number;
  is_active: boolean;
  created_at: string;
  member_count?: number;
  captain_display_name?: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  profile_id: string;
  role: 'captain' | 'co_captain' | 'member';
  joined_at: string;
  display_name?: string;
  handle?: string;
  avatar_url?: string;
}

/* ------------------------------------------------------------------ */
/*  CRUD                                                               */
/* ------------------------------------------------------------------ */

/** Create a team and insert the creator as captain. Returns the new team. */
export async function createTeam(
  name: string,
  tag: string | null,
  description: string | null,
  captainId: string,
): Promise<Team> {
  if (isRateLimited('team_create', 3, 3_600_000)) {
    throw new Error('Too many requests. Please wait a moment.');
  }
  const cleanTag = tag?.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5) || null;

  const { data: team, error } = await supabase
    .from('teams')
    .insert({
      name: name.trim(),
      tag: cleanTag,
      description: description?.trim() || null,
      captain_id: captainId,
    })
    .select('*')
    .single();

  if (error) throw error;

  // Insert captain as first member
  const { error: memberErr } = await supabase.from('team_members').insert({
    team_id: team.id,
    profile_id: captainId,
    role: 'captain',
  });

  if (memberErr) throw memberErr;

  return team as Team;
}

/** Fetch all teams the given profile belongs to. */
export async function fetchMyTeams(profileId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select('team_id, role, teams(*)')
    .eq('profile_id', profileId);

  if (error) throw error;
  if (!data) return [];

  return data
    .filter((row: any) => row.teams)
    .map((row: any) => ({ ...row.teams } as Team));
}

/** Fetch members of a team with profile info. */
export async function fetchTeamMembers(teamId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select('id, team_id, profile_id, role, joined_at, profiles(display_name, handle, avatar_url)')
    .eq('team_id', teamId)
    .order('role', { ascending: true })
    .order('joined_at', { ascending: true });

  if (error) throw error;
  if (!data) return [];

  return data.map((row: any) => ({
    id: row.id,
    team_id: row.team_id,
    profile_id: row.profile_id,
    role: row.role,
    joined_at: row.joined_at,
    display_name: row.profiles?.display_name ?? undefined,
    handle: row.profiles?.handle ?? undefined,
    avatar_url: row.profiles?.avatar_url ?? undefined,
  }));
}

/** Join a team as a regular member. */
export async function joinTeam(teamId: string, profileId: string): Promise<void> {
  const { error } = await supabase.from('team_members').insert({
    team_id: teamId,
    profile_id: profileId,
    role: 'member',
  });
  if (error) throw error;
}

/** Leave a team (remove membership row). */
export async function leaveTeam(teamId: string, profileId: string): Promise<void> {
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('profile_id', profileId);
  if (error) throw error;
}

/** Search teams by name or tag. */
export async function searchTeams(query: string): Promise<Team[]> {
  const q = query.trim();
  if (!q) {
    // Return recent active teams when no query
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) throw error;
    return (data ?? []) as Team[];
  }

  const pattern = `%${q}%`;
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('is_active', true)
    .or(`name.ilike.${pattern},tag.ilike.${pattern}`)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []) as Team[];
}
